import { Button } from '@thanx/uikit/button'
import { Modal } from '@thanx/uikit/modal'
import { Popover } from '@thanx/uikit/popover'
import { Switch } from '@thanx/uikit/switch'
import { getCampaignMetricByType } from 'actions/campaignMetrics'
import { scheduleCampaign, updateCampaign } from 'actions/campaigns'
import { getTargetSegment } from 'actions/targetSegments'
import approvalImage from 'assets/images/campaign-center/img_approval_required.svg'
import automatedImage from 'assets/images/campaign-center/img_launch.png'
import scheduleImage from 'assets/images/campaign-center/img_schedule.png'
import sendImage from 'assets/images/campaign-center/img_send.png'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import useCanMessageEveryone from 'hooks/useCanMessageEveryone'
import useCurrentMerchantUser from 'hooks/useCurrentMerchantUser'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import { Fields as Campaign } from 'models/Campaign'
import { Type as MetricType, UserCountMetric } from 'models/CampaignMetric'
import moment from 'moment-timezone'
import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button as BSButton,
  Col,
  OverlayTrigger,
  Row,
} from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { I18n, Translate } from 'react-redux-i18n'
import ContentWithSave from 'scenes/CampaignCenter/Builder/components/ContentWithSave'
import { selectCampaignMetricByType } from 'selectors/campaignMetric'
import { selectCampaignTypeByType } from 'selectors/campaignType'
import { selectTargetSegmentById } from 'selectors/targetSegment'
import { useAbility } from 'utilities/ability'
import userTimeZone from 'utilities/userTimeZone'
import { useBuilderReadOnly } from '../CampaignConfigContext'
import Approvers from './Approvers'
import IncludeExistingSegmentModal from './IncludeExistingSegmentModal'

type Props = {
  campaign: Campaign
  disabled: boolean
  isSaving: boolean
  triggerSave: () => Promise<void>
  setCurrentStep: (step: string) => void
}

const t = buildTranslate('thanx_campaigns.builder.confirm_modal')

const Help: React.FC<{ sendType: string }> = ({ sendType }) => {
  if (sendType !== 'schedule') return null
  const popover = <Popover id="info">{t('schedule.info')}</Popover>
  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      overlay={popover}
      placement="top"
    >
      <i className="fa fa-question-circle grey-60" />
    </OverlayTrigger>
  )
}

const Confirmation: React.FC<Props> = props => {
  const { campaign, triggerSave, setCurrentStep, disabled, isSaving } = props
  const [modalOpen, setModalOpen] = useState(false)
  const [errored, setErrored] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [includeExistingModalOpen, setIncludeExistingModalOpen] =
    useState(false)
  const [includeExistingError, setIncludeExistingError] = useState(false)
  const [includeExistingSubmitting, setIncludeExistingSubmitting] =
    useState(false)

  const ability = useAbility()
  const dispatch = useDispatch()
  const canMessageEveryone = useCanMessageEveryone()
  const currentMerchantUser = useCurrentMerchantUser()
  const userCountMetric = useSelector(
    state =>
      selectCampaignMetricByType(
        state,
        campaign.id,
        MetricType.USER_COUNT
      ) as UserCountMetric | null
  )
  const campaignType = useSelector(state =>
    selectCampaignTypeByType(state, campaign.type)
  )
  const targetSegment = useSelector(
    selectTargetSegmentById(campaign.config_target_segment_id)
  )

  // Fetch the specific target segment if campaign has one
  useEffect(() => {
    if (campaign.config_target_segment_id) {
      dispatch(getTargetSegment(campaign.config_target_segment_id))
    }
  }, [dispatch, campaign.config_target_segment_id])

  const category = campaignType?.category
  const sendType = getSendType()
  const forceRequestReview = campaign.type === 'message' && !canMessageEveryone

  // Check if campaign uses an inactive segment (any state other than visible)
  const isSegmentInactive = targetSegment && targetSegment.state !== 'visible'

  // Determine if we should show the include existing segment modal
  // Show when API recommends including existing users but it's not yet enabled
  const shouldShowIncludeExistingModal =
    campaign.type === 'custom_automated' &&
    campaign.recommendations?.include_existing_users === true &&
    !campaign.config_include_existing_users

  function getSendType() {
    // Non-admin location-scoped users cannot send/approve message-to-everyone
    if (campaign.type === 'message' && !canMessageEveryone) {
      return 'request_review'
    }
    if (ability.cannot('review', 'Campaign')) {
      if (campaign.type === 'custom_automated') return 'custom_automated_review'
      return 'request_review'
    }
    if (
      campaign.state === 'in_review' &&
      //@ts-ignore
      currentMerchantUser.isApproverFor(campaign)
    ) {
      return 'approve'
    }
    if (campaign.type === 'custom_automated') return 'custom_automated'
    if (category === 'automated') return 'automated'
    return campaign.start_at ? 'schedule' : 'send_now'
  }

  function handleOpenClick() {
    triggerSave()
    setErrored(false)

    if (!userCountMetric) {
      dispatch(getCampaignMetricByType(campaign.id, 'user_count'))
    }

    // Show include existing segment modal first if conditions apply
    if (shouldShowIncludeExistingModal) {
      setIncludeExistingModalOpen(true)
    } else {
      setModalOpen(true)
    }
  }

  function closeModal() {
    setModalOpen(false)
    setErrored(false)
  }

  function handleIncludeExistingExclude() {
    // User chose to leave out existing segment users, proceed to main modal
    setIncludeExistingModalOpen(false)
    setModalOpen(true)
  }

  async function handleIncludeExistingInclude() {
    // User chose to include existing segment users
    setIncludeExistingSubmitting(true)
    setIncludeExistingError(false)

    const response = await dispatch(
      updateCampaign(campaign.id, {
        campaign: { config_include_existing_users: true },
      })
    )
    setIncludeExistingSubmitting(false)

    //@ts-ignore
    if (response.error) {
      setIncludeExistingError(true)
      return
    }

    setIncludeExistingModalOpen(false)
    setModalOpen(true)
  }

  function formatTime(time: string): string {
    return moment(time).tz(userTimeZone()).format('LL [at] LT z')
  }

  async function getScheduleCampaign() {
    setSubmitting(true)

    const response = await dispatch(scheduleCampaign(campaign.id))
    setSubmitting(false)
    //@ts-ignore
    if (response.error) {
      setErrored(true)
      return
    }
    closeModal()
    setCurrentStep('success')
  }

  function onCancelClick() {
    closeModal()
  }

  const hasValidUserCount =
    userCountMetric?.data?.count !== undefined &&
    !isNaN(userCountMetric.data.count)
  const countText = !userCountMetric
    ? '<i class="fa fa-spin fa-circle-o-notch"></i>'
    : `<strong class="grey-90">
      ${I18n.l(userCountMetric?.data?.count)}
    </strong>`

  let imgSrc: undefined | string
  let text: React.ReactNode | null = null

  switch (sendType) {
    case 'custom_automated_review':
      imgSrc = approvalImage
      text = (
        <div className="text-left mb-s">
          <div
            className="body-text-3 grey-70-text mb-m"
            dangerouslySetInnerHTML={{
              __html: t('custom_automated.body', {
                segment: campaign.config_target_name,
              }),
            }}
          />
          <Row>
            <Col sm={6}>
              <div className="small-caps-6 bold grey-90-text mb-xs">
                {t('custom_automated.start')}
              </div>
              <div className="body-text-4 grey-90-text">
                {campaign.start_at
                  ? formatTime(campaign.start_at)
                  : t('custom_automated.now')}
              </div>
            </Col>
            <Col sm={6}>
              <div className="small-caps-6 bold grey-90-text mb-xs">
                {t('custom_automated.stop')}
              </div>
              <div className="body-text-4 grey-90-text">
                {campaign.end_at
                  ? formatTime(campaign.end_at)
                  : t('custom_automated.ongoing')}
              </div>
            </Col>
          </Row>
        </div>
      )
      break
    case 'request_review':
      imgSrc = approvalImage
      text = (
        <div className="width-66 ">
          <p className="margin-bottom-medium">
            <Translate
              value={
                hasValidUserCount
                  ? 'thanx_campaigns.builder.confirm_modal.request_review.body'
                  : 'thanx_campaigns.builder.confirm_modal.request_review.body_no_count'
              }
              count={countText}
              sendAtWithTime={formatTime(campaign.start_at)}
              dangerousHTML
            />
            &nbsp;
            <Help sendType={sendType} />
          </p>
        </div>
      )
      break
    case 'approve':
      imgSrc = approvalImage
      text = (
        <div className="width-66 ">
          <p className="margin-bottom-medium">
            <Translate
              value={
                hasValidUserCount
                  ? 'thanx_campaigns.builder.confirm_modal.approve.body'
                  : 'thanx_campaigns.builder.confirm_modal.approve.body_no_count'
              }
              count={countText}
              sendAtWithTime={formatTime(campaign.start_at)}
              dangerousHTML
            />
            &nbsp;
            <Help sendType={sendType} />
          </p>
        </div>
      )
      break
    case 'send_now':
      imgSrc = sendImage
      text = (
        <div className="width-66 ">
          <p className="margin-bottom-medium">
            <Translate
              value={
                hasValidUserCount
                  ? 'thanx_campaigns.builder.confirm_modal.send_now.body'
                  : 'thanx_campaigns.builder.confirm_modal.send_now.body_no_count'
              }
              count={countText}
              dangerousHTML
            />
            &nbsp;
            <Help sendType={sendType} />
          </p>
          <Translate
            tag="p"
            value="thanx_campaigns.builder.confirm_modal.send_now.sub_text"
            dangerousHTML
          />
        </div>
      )
      break
    case 'schedule':
      imgSrc = scheduleImage
      text = (
        <div className="width-66 ">
          <p className="margin-bottom-medium">
            <Translate
              value={
                hasValidUserCount
                  ? 'thanx_campaigns.builder.confirm_modal.schedule.body'
                  : 'thanx_campaigns.builder.confirm_modal.schedule.body_no_count'
              }
              count={countText}
              sendAtWithTime={formatTime(campaign.start_at)}
              dangerousHTML
            />
            &nbsp;
            <Help sendType={sendType} />
          </p>
          <Translate
            tag="p"
            value="thanx_campaigns.builder.confirm_modal.schedule.sub_text"
            dangerousHTML
          />
        </div>
      )
      break
    case 'automated':
      imgSrc = automatedImage
      text = (
        <div className="width-66 ">
          <p className="margin-bottom-medium">
            <Translate
              value={`thanx_campaigns.builder.confirm_modal.automated.body.${campaign.type}`}
              sendAt={moment(campaign.start_at).tz(userTimeZone()).format('L')}
              dangerousHTML
            />
            &nbsp;
            <Help sendType={sendType} />
          </p>
          <Translate
            tag="p"
            value={`thanx_campaigns.builder.confirm_modal.automated.sub_text.${campaign.type}`}
            dangerousHTML
          />
        </div>
      )
      break
    case 'custom_automated':
      imgSrc = automatedImage
      text = (
        <div className="text-left mb-s">
          <div
            className="body-text-3 grey-70-text mb-m"
            dangerouslySetInnerHTML={{
              __html: t('custom_automated.body', {
                segment: campaign.config_target_name,
              }),
            }}
          />
          <Row>
            <Col sm={6}>
              <div className="small-caps-6 bold grey-90-text mb-xs">
                {t('custom_automated.start')}
              </div>
              <div className="body-text-4 grey-90-text">
                {campaign.start_at
                  ? formatTime(campaign.start_at)
                  : t('custom_automated.now')}
              </div>
            </Col>
            <Col sm={6}>
              <div className="small-caps-6 bold grey-90-text mb-xs">
                {t('custom_automated.stop')}
              </div>
              <div className="body-text-4 grey-90-text">
                {campaign.end_at
                  ? formatTime(campaign.end_at)
                  : t('custom_automated.ongoing')}
              </div>
            </Col>
          </Row>
        </div>
      )
      break
    default:
      break
  }

  const builderReadOnly = useBuilderReadOnly()
  const isButtonDisabled = disabled || isSegmentInactive || builderReadOnly

  const messageTooltipReason = forceRequestReview
    ? I18n.t('thanx_campaigns.newCampaign.message.admin_or_global_required')
    : undefined

  return (
    <div className="inline-block">
      <PermissionTooltip
        disabled={forceRequestReview}
        reason={messageTooltipReason}
        id="confirmation-message-tooltip"
        placement="bottom"
      >
        <BSButton
          bsSize="large"
          className="btn-inverted"
          disabled={isButtonDisabled || forceRequestReview}
          onClick={handleOpenClick}
        >
          <ContentWithSave
            value={`thanx_campaigns.builder.steps.nav.buttons.${sendType}`}
            isSaving={isSaving}
          />
        </BSButton>
      </PermissionTooltip>
      <IncludeExistingSegmentModal
        isOpen={includeExistingModalOpen}
        onClose={() => setIncludeExistingModalOpen(false)}
        onExclude={handleIncludeExistingExclude}
        onInclude={handleIncludeExistingInclude}
        isLoading={includeExistingSubmitting}
        error={includeExistingError}
      />
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        size="auto"
        closeType="small"
        zIndex={1200}
      >
        <div
          className="fs-unmask text-center w-100 p-xl"
          style={{ width: '600px' }}
        >
          <h3 className="text-center margin-bottom-huge">
            {t(`${sendType}.title`, {
              campaignName: `${sendType}.title_campaign_name.${campaignType}`,
            })}
          </h3>
          <Switch condition={!!errored}>
            <Alert bsStyle="danger">{t('error')}</Alert>
          </Switch>
          <img
            src={imgSrc}
            alt="campaign type"
            className="margin-bottom-huge"
          />

          <div className="font-size-18 text-center grey-60 padding-bottom-medium">
            <div className="d-flex justify-content-center">{text}</div>
            {(ability.cannot('review', 'Campaign') || forceRequestReview) && (
              <Approvers
                onCancelClick={onCancelClick}
                closeModal={closeModal}
                campaignId={campaign.id}
                onApprove={() => setCurrentStep('success')}
              />
            )}
          </div>
          {ability.can('review', 'Campaign') && !forceRequestReview && (
            <Row className="mt-xxl">
              <Col sm={6}>
                <Button
                  className="w-100"
                  kind="secondary"
                  onClick={onCancelClick}
                >
                  {t(`${sendType}.buttons.exit`)}
                </Button>
              </Col>
              <Col sm={6}>
                <Button
                  onClick={() => getScheduleCampaign()}
                  className="w-100"
                  isLoading={submitting}
                >
                  {t(`${sendType}.buttons.finish`)}
                </Button>
              </Col>
            </Row>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default Confirmation
